import React, { useRef } from 'react'

import { Widget } from '@frame/ui-primitives/widget'

import { SandboxCredentials } from '@credentials/connector-screen/sandbox/sandbox-creds'
import { FullScreenDropzone } from '@frame/components/screens/tasks/screens/strategies/documents/components/full-screen-dropzone'
import { useStore } from '@frame/lib/stores/useStore'
import { DataAttribute } from '@frame/types'
import { DialogPanel, Dialog as HUIDialog, Transition, TransitionChild } from '@headlessui/react'
import { Env } from '@script/types'

import { useGetFileUploadContext } from './screens/tasks/screens/strategies/documents/hooks/useGetFileUploadContext'

type DialogProps = {
  children: React.ReactNode
}

export function Dialog({ children }: DialogProps) {
  // This is used to disable focusing on the close button in the header when the dialog is opened
  const panelRef = useRef<HTMLDivElement>(null)
  const { env } = useStore((state) => ({
    env: state.config.env,
    consumerInitiated: state.session?.consumer_initiated,
  }))
  const { dragActive, setDragActive } = useGetFileUploadContext()

  return (
    <FullScreenDropzone>
      <Transition show appear unmount={false}>
        <HUIDialog
          initialFocus={panelRef}
          data-cy={DataAttribute.DIALOG}
          open
          as="div"
          className="relative"
          onClose={() => void {}}
          onMouseLeave={() => dragActive && setDragActive(false)}
        >
          <div className="fixed inset-0 hidden sm:block sm:bg-backdrop sm:bg-opacity-90" />
          <div className="fixed inset-0 overflow-hidden">
            <div className="flex min-h-full items-center justify-center">
              <TransitionChild
                as="div"
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-[48px]"
                enterTo="opacity-100 translate-y-0"
                className="w-full"
              >
                <DialogPanel ref={panelRef}>
                  <Widget>
                    {children}
                    {env === Env.Sandbox && (
                      <div className="absolute inset-0 z-50 mx-auto flex h-[24px] w-fit items-center justify-center rounded-b-sm bg-moss-500 p-3 text-xs text-moss-100">
                        Sandbox Environment
                      </div>
                    )}
                  </Widget>
                </DialogPanel>
              </TransitionChild>
            </div>
          </div>
          <SandboxCredentials />
        </HUIDialog>
      </Transition>
    </FullScreenDropzone>
  )
}

export function DialogForError({ children }: DialogProps) {
  const { dragActive, setDragActive } = useGetFileUploadContext()

  return (
    <Transition show>
      <HUIDialog
        onClose={() => void {}}
        data-cy={DataAttribute.DIALOG}
        open
        as="div"
        className="relative"
        onMouseLeave={() => dragActive && setDragActive(false)}
      >
        <TransitionChild
          as="div"
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-300"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
          className="fixed inset-0 hidden sm:block sm:bg-backdrop sm:bg-opacity-90"
        />
        <div className="fixed inset-0 overflow-hidden">
          <div className="flex min-h-full items-center justify-center">
            <TransitionChild
              as="div"
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-75"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-300"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-75"
              className="w-full"
            >
              <DialogPanel>
                <Widget>{children}</Widget>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </HUIDialog>
    </Transition>
  )
}
